import type { Nullable, RGBA } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { ImageRectComponent } from "../image-rect-component";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { CropSelectionBottomComponent } from "./crop-selection-bottom-component";
import { CropSelectionBottomLeftComponent } from "./crop-selection-bottom-left-component";
import { CropSelectionBottomRightComponent } from "./crop-selection-bottom-right-component";
import { CropSelectionLeftComponent } from "./crop-selection-left-component";
import { CropSelectionRightComponent } from "./crop-selection-right-component";
import { CropSelectionTopComponent } from "./crop-selection-top-component";
import { CropSelectionTopLeftComponent } from "./crop-selection-top-left-component";
import { CropSelectionTopRightComponent } from "./crop-selection-top-right-component";
import { Mat3 } from "utils/math/mat3";
import CanvasLineUnitComponent from "../canvas-resize-component/canvas-line-unit-component";
import CropUnitComponent from "./crop-unit-component";

const CROP_CENTER_SNAP_DISTANCE_PX = 10;
const CROP_CENTER_SNAP_RELEASE_DISTANCE_PX = 16;
const CROP_CENTER_GUIDE_THICKNESS_PX = 2;
const CROP_CENTER_CROSSHAIR_LENGTH_PX = 22;
const CROP_CENTER_CROSSHAIR_OUTER_THICKNESS_PX = 3;
const CROP_CENTER_CROSSHAIR_INNER_THICKNESS_PX = 1;
// Keep the same accent color as selection/crop UI (see selection-grid shader).
const CROP_SELECTION_ACCENT_COLOR: RGBA = [0, 0.6, 0.63, 1];
const CROP_CENTER_CROSSHAIR_OUTER_COLOR: RGBA = [0, 0.6, 0.63, 0.35];
const CROP_CENTER_CROSSHAIR_INNER_COLOR: RGBA = CROP_SELECTION_ACCENT_COLOR;

export class CropSelectionComponent extends Webgl2Component {
  private _tm3: Mat3;
  private _centerPoint: Vec2;
  private _mouseDownPosition = Vec2.zero();

  private _centerCrosshairParts: Webgl2Component[] = [];
  private _isCenterCrosshairVisible = false;

  private _canvasCenterVGuide: Nullable<CanvasLineUnitComponent> = null;
  private _canvasCenterHGuide: Nullable<CanvasLineUnitComponent> = null;
  private _isSnappedToCenterX = false;
  private _isSnappedToCenterY = false;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();

    const topLeftComponent = new CropSelectionTopLeftComponent(this._renderer, this);
    topLeftComponent.style.top = "0";
    topLeftComponent.style.left = "0";
    topLeftComponent.style.translateX = "-2px";
    topLeftComponent.style.translateY = "-2px";
    this.appendChild(topLeftComponent);

    const topComponent = new CropSelectionTopComponent(this._renderer, this);
    topComponent.style.top = "0";
    topComponent.style.left = "50%";
    topComponent.style.translateX = "-50%";
    topComponent.style.translateY = "-50%";
    this.appendChild(topComponent);

    const topRightComponent = new CropSelectionTopRightComponent(this._renderer, this);
    topRightComponent.style.top = "0";
    topRightComponent.style.left = "100%";
    topRightComponent.style.translateX = "-100%&2px";
    topRightComponent.style.translateY = "-2px";
    this.appendChild(topRightComponent);

    const leftComponent = new CropSelectionLeftComponent(this._renderer, this);
    leftComponent.style.top = "50%";
    leftComponent.style.left = "0";
    leftComponent.style.translateX = "-50%";
    leftComponent.style.translateY = "-50%";
    this.appendChild(leftComponent);

    const rightComponent = new CropSelectionRightComponent(this._renderer, this);
    rightComponent.style.top = "50%";
    rightComponent.style.left = "100%";
    rightComponent.style.translateX = "-50%";
    rightComponent.style.translateY = "-50%";
    this.appendChild(rightComponent);

    const bottomLeftComponent = new CropSelectionBottomLeftComponent(this._renderer, this);
    bottomLeftComponent.style.top = "100%";
    bottomLeftComponent.style.left = "0";
    bottomLeftComponent.style.translateX = "-2px";
    bottomLeftComponent.style.translateY = "-100%&2px";
    this.appendChild(bottomLeftComponent);

    const bottomComponent = new CropSelectionBottomComponent(this._renderer, this);
    bottomComponent.style.top = "100%";
    bottomComponent.style.left = "50%";
    bottomComponent.style.translateX = "-50%";
    bottomComponent.style.translateY = "-50%";
    this.appendChild(bottomComponent);

    const bottomRightComponent = new CropSelectionBottomRightComponent(this._renderer, this);
    bottomRightComponent.style.top = "100%";
    bottomRightComponent.style.left = "100%";
    bottomRightComponent.style.translateX = "-100%&2px";
    bottomRightComponent.style.translateY = "-100%&2px";
    this.appendChild(bottomRightComponent);

    this._appendCenterCrosshair();

    // TODO:?
    // NOTE: This is crutch to update component on scale change, since component doesn't have
    // relation to camera change
    this._renderer.camera.events.on("scale", (newScale: number) => {
      const value = 1 / newScale;
      this.forEachChildren((child) => {
        child.style.scaleX = value * window.devicePixelRatio;
        child.style.scaleY = value * window.devicePixelRatio;
      });
    });
  }

  public setCenterCrosshairVisible(visible: boolean) {
    if (this._isCenterCrosshairVisible === visible) return;
    this._isCenterCrosshairVisible = visible;

    const display = visible ? "initial" : "none";
    this._centerCrosshairParts.forEach((part) => (part.style.display = display));
    this._renderer.render();
  }

  private _appendCenterCrosshair() {
    const centerBaseStyles = (component: Webgl2Component) => {
      component.style.top = "50%";
      component.style.left = "50%";
      component.style.translateX = "-50%";
      component.style.translateY = "-50%";
      component.style.pointerEvents = "none";
    };

    const appendCrosshairPart = (component: Webgl2Component) => {
      centerBaseStyles(component);
      component.style.display = this._isCenterCrosshairVisible ? "initial" : "none";
      this._centerCrosshairParts.push(component);
      this.appendChild(component);
    };

    const vOuter = new CropUnitComponent(
      new Vec2(CROP_CENTER_CROSSHAIR_OUTER_THICKNESS_PX, CROP_CENTER_CROSSHAIR_LENGTH_PX),
      CROP_CENTER_CROSSHAIR_OUTER_COLOR,
    );
    appendCrosshairPart(vOuter);

    const vInner = new CropUnitComponent(
      new Vec2(CROP_CENTER_CROSSHAIR_INNER_THICKNESS_PX, CROP_CENTER_CROSSHAIR_LENGTH_PX),
      CROP_CENTER_CROSSHAIR_INNER_COLOR,
    );
    appendCrosshairPart(vInner);

    const hOuter = new CropUnitComponent(
      new Vec2(CROP_CENTER_CROSSHAIR_LENGTH_PX, CROP_CENTER_CROSSHAIR_OUTER_THICKNESS_PX),
      CROP_CENTER_CROSSHAIR_OUTER_COLOR,
    );
    appendCrosshairPart(hOuter);

    const hInner = new CropUnitComponent(
      new Vec2(CROP_CENTER_CROSSHAIR_LENGTH_PX, CROP_CENTER_CROSSHAIR_INNER_THICKNESS_PX),
      CROP_CENTER_CROSSHAIR_INNER_COLOR,
    );
    appendCrosshairPart(hInner);
  }

  public copyComponentTransform(component: Nullable<Webgl2Component<any, any>>) {
    if (!component) return;
    this.transform.copy(component.transform);
  }

  public clipTransform() {
    if (!this._selectedComponent) return;

    this.transform.size.clamp(Vec2.zero(), this._selectedComponent.transform.size);

    const minTranslation = this._selectedComponent.transform.translation;
    const maxTranslation = minTranslation
      .clone()
      .add(this._selectedComponent.transform.size)
      .sub(this.transform.size);
    this.transform.translation.clamp(minTranslation, maxTranslation);
  }

  public onMouseDown(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    const selectedTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.zero(),
    );
    const centerPoint = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(this, Vec2.half());
    this._tm3 = Mat3.identity()
      .rotate(-this._selectedComponent.transform.rads)
      .translate(selectedTopLeft.clone().negate());
    this._mouseDownPosition = this._renderer.camera
      .getGlobalMousePixelPosition(e)
      .transformMat3(this._tm3.mat);
    this._centerPoint = centerPoint.transformMat3(this._tm3.mat);
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    const currentMousePos = this._renderer.camera
      .getGlobalMousePixelPosition(e)
      .transformMat3(this._tm3.mat);
    const diff = currentMousePos.clone().sub(this._mouseDownPosition);

    const halfCropSize = this.transform.size.clone().divide(2);

    // Canvas bounds (global) converted to selected-component local space.
    const canvasTopLeftLocal = this._renderer.canvasTranslation.transformMat3(this._tm3.mat);
    const canvasBottomRightLocal = this._renderer.canvasTranslation
      .clone()
      .add(this._renderer.canvasSize)
      .transformMat3(this._tm3.mat);

    const minX = Math.min(canvasTopLeftLocal.x, canvasBottomRightLocal.x) + halfCropSize.x;
    const maxX = Math.max(canvasTopLeftLocal.x, canvasBottomRightLocal.x) - halfCropSize.x;
    const minY = Math.min(canvasTopLeftLocal.y, canvasBottomRightLocal.y) + halfCropSize.y;
    const maxY = Math.max(canvasTopLeftLocal.y, canvasBottomRightLocal.y) - halfCropSize.y;

    const scale = this._renderer.camera.scale || 1;
    const snapIn = CROP_CENTER_SNAP_DISTANCE_PX / scale;
    const snapOut = CROP_CENTER_SNAP_RELEASE_DISTANCE_PX / scale;

    const canvasCenterLocal = this._renderer.canvasGlobalCenter.transformMat3(this._tm3.mat);

    const nextCenter = this._centerPoint
      .clone()
      .add(diff)
      .clamp(new Vec2(minX, minY), new Vec2(maxX, maxY));

    const dx = Math.abs(nextCenter.x - canvasCenterLocal.x);
    const dy = Math.abs(nextCenter.y - canvasCenterLocal.y);

    const shouldSnapX = this._isSnappedToCenterX ? dx <= snapOut : dx <= snapIn;
    const shouldSnapY = this._isSnappedToCenterY ? dy <= snapOut : dy <= snapIn;

    this._isSnappedToCenterX = shouldSnapX;
    this._isSnappedToCenterY = shouldSnapY;

    if (shouldSnapX) nextCenter.setX(canvasCenterLocal.x);
    if (shouldSnapY) nextCenter.setY(canvasCenterLocal.y);

    this._updateCanvasCenterGuides(shouldSnapX, shouldSnapY);

    this.transform.calcDimensions({
      position: nextCenter.transformMat3(this._tm3.clone().invert().mat),
    });
    this._renderer.render();
  }

  public onMouseUp(e: MouseEvent | TouchEvent) {
    void e;
    this._isSnappedToCenterX = false;
    this._isSnappedToCenterY = false;
    this._hideCanvasCenterGuides();
  }

  private _hideCanvasCenterGuides() {
    if (this._canvasCenterVGuide) this._canvasCenterVGuide.style.display = "none";
    if (this._canvasCenterHGuide) this._canvasCenterHGuide.style.display = "none";
  }

  private _updateCanvasCenterGuides(showV: boolean, showH: boolean) {
    if (!this._selectedComponent) return this._hideCanvasCenterGuides();

    const scale = this._renderer.camera.scale || 1;
    const thickness = CROP_CENTER_GUIDE_THICKNESS_PX / scale;
    const canvasCenterGlobal = this._renderer.canvasGlobalCenter;
    const canvasSize = this._renderer.canvasSize.clone();

    if (showV) {
      const size = new Vec2(thickness, canvasSize.height);
      if (!this._canvasCenterVGuide) {
        this._canvasCenterVGuide = new CanvasLineUnitComponent(size);
        this._canvasCenterVGuide.style.display = "none";
        this._renderer.addComponent(this._canvasCenterVGuide);
      }
      this._canvasCenterVGuide.transform.calcDimensions({
        position: canvasCenterGlobal,
        size,
        rotation: 0,
        roundSize: false,
      });
      this._canvasCenterVGuide.style.display = "initial";
    } else if (this._canvasCenterVGuide) {
      this._canvasCenterVGuide.style.display = "none";
    }

    if (showH) {
      const size = new Vec2(canvasSize.width, thickness);
      if (!this._canvasCenterHGuide) {
        this._canvasCenterHGuide = new CanvasLineUnitComponent(size);
        this._canvasCenterHGuide.style.display = "none";
        this._renderer.addComponent(this._canvasCenterHGuide);
      }
      this._canvasCenterHGuide.transform.calcDimensions({
        position: canvasCenterGlobal,
        size,
        rotation: 0,
        roundSize: false,
      });
      this._canvasCenterHGuide.style.display = "initial";
    } else if (this._canvasCenterHGuide) {
      this._canvasCenterHGuide.style.display = "none";
    }
  }

  public getRenderData() {
    if (!this._renderer.hasSelectedComponent) return;

    const material = Webgl2.materials.selectionGrid;
    material.setUniforms({
      u_size: this.transform.size.getAbs().getArray(),
      u_showGrid: true,
    });
    return { material };
  }
}

export default CropSelectionComponent;
